import { HttpClient } from "@utils/httpClient";
import type {
  CredentialWrite,
  Deprecation,
  DeprecationUpsert,
  ModelCatalog,
  ModelPrice,
  ModuleOption,
  PriceEntry,
  ProviderCatalog,
  ProviderCredential,
} from "@models/providerCatalog";
import { ApiError } from "@models/error";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL;

const pricePath = (providerKey: string, modelId: string) =>
  `/providers/${encodeURIComponent(providerKey)}/prices/${encodeURIComponent(modelId)}`;

const credentialsPath = (providerKey: string) => `/providers/${encodeURIComponent(providerKey)}/credentials`;
const credentialPath = (providerKey: string, credentialId: string) =>
  `${credentialsPath(providerKey)}/${encodeURIComponent(credentialId)}`;

/** Supported providers and their credentials, all in core. */
class ProviderCatalogApi {
  private readonly http: HttpClient;

  constructor() {
    if (!BASE_URL) throw new Error("VITE_XPLATFORM_CORE_API_BASE_URL is not configured");
    this.http = new HttpClient(BASE_URL);
  }

  catalog(signal?: AbortSignal): Promise<ProviderCatalog> {
    return this.http.makeAuthenticatedRequest<ProviderCatalog>("/admin/providers", { method: "GET", signal });
  }

  /** `include_library` adds every model genai-prices prices for the providers in use. */
  models(filters: { provider?: string; capability?: string; days?: number; include_library?: boolean } = {},
         signal?: AbortSignal): Promise<ModelCatalog> {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => v).map(([k, v]) => [k, String(v)]) as Array<[string, string]>,
    );
    const query = params.toString();
    return this.http.makeAuthenticatedRequest<ModelCatalog>(`/admin/models${query ? `?${query}` : ""}`, {
      method: "GET",
      signal,
    });
  }

  /** The BeeHive modules a credential can be granted to (created under Access > Modules). */
  modules(signal?: AbortSignal): Promise<ModuleOption[]> {
    return this.http.makeAuthenticatedRequest<ModuleOption[]>("/admin/providers/modules", { method: "GET", signal });
  }

  /** A new credential, written to SSM for every module in `body.modules`. */
  createCredential(providerKey: string, body: CredentialWrite): Promise<ProviderCredential> {
    return this.http.makeAuthenticatedRequest<ProviderCredential>(credentialsPath(providerKey), {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  /** Replace a credential (the key again) and, with `modules`, the modules it is granted to. */
  updateCredential(providerKey: string, credentialId: string, body: CredentialWrite): Promise<ProviderCredential> {
    return this.http.makeAuthenticatedRequest<ProviderCredential>(credentialPath(providerKey, credentialId), {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  /** The admin price of a model, or `null` when it has none (it then uses the library price, if any). */
  async price(providerKey: string, modelId: string, signal?: AbortSignal): Promise<ModelPrice | null> {
    try {
      return await this.http.makeAuthenticatedRequest<ModelPrice>(pricePath(providerKey, modelId), { method: "GET", signal });
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) return null;
      throw error;
    }
  }

  /** Add a price entry (or replace the one with the same date), keeping the rest of the history. */
  addPriceEntry(providerKey: string, modelId: string, entry: PriceEntry): Promise<ModelPrice> {
    return this.http.makeAuthenticatedRequest<ModelPrice>(`${pricePath(providerKey, modelId)}/entries`, {
      method: "POST",
      body: JSON.stringify(entry),
    });
  }

  /** Replace the whole history (used to remove one entry). */
  replacePrice(providerKey: string, modelId: string, body: { note?: string | null; prices: PriceEntry[] }): Promise<ModelPrice> {
    return this.http.makeAuthenticatedRequest<ModelPrice>(pricePath(providerKey, modelId), {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  /** Remove the admin price: the model falls back to the library price, or becomes unpriced. */
  deletePrice(providerKey: string, modelId: string): Promise<void> {
    return this.http.makeAuthenticatedRequest<void>(pricePath(providerKey, modelId), { method: "DELETE" });
  }

  deprecations(signal?: AbortSignal): Promise<Deprecation[]> {
    return this.http.makeAuthenticatedRequest<Deprecation[]>("/admin/models/deprecations", { method: "GET", signal });
  }

  saveDeprecation(providerKey: string, modelId: string, body: DeprecationUpsert): Promise<Deprecation> {
    return this.http.makeAuthenticatedRequest<Deprecation>(
      `/admin/models/deprecations/${encodeURIComponent(providerKey)}/${encodeURIComponent(modelId)}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  }

  deleteDeprecation(providerKey: string, modelId: string): Promise<void> {
    return this.http.makeAuthenticatedRequest<void>(
      `/admin/models/deprecations/${encodeURIComponent(providerKey)}/${encodeURIComponent(modelId)}`,
      { method: "DELETE" },
    );
  }

  /** Remove the credential from every module it is granted to. */
  deleteCredential(providerKey: string, credentialId: string): Promise<void> {
    return this.http.makeAuthenticatedRequest<void>(credentialPath(providerKey, credentialId), { method: "DELETE" });
  }
}

let instance: ProviderCatalogApi | null = null;
export const providerCatalogApi = (): ProviderCatalogApi => {
  instance ??= new ProviderCatalogApi();
  return instance;
};
